
import { DataSourceMode } from '../types';

interface RequestOptions extends Omit<RequestInit, 'body'> {
  mockDelay?: number; // Simulate network delay
  params?: Record<string, string>; // Query parameters
  body?: any;
}

class ApiClient {
  private baseUrl: string;
  private getAuthToken: () => string | null;
  private getMode: () => DataSourceMode;
  private getLanguage: () => string;

  constructor(
    baseUrl: string, 
    getAuthToken: () => string | null, 
    getMode: () => DataSourceMode,
    getLanguage: () => string
  ) {
    this.baseUrl = baseUrl;
    this.getAuthToken = getAuthToken;
    this.getMode = getMode;
    this.getLanguage = getLanguage;
  }

  /**
   * Universal executor that decides whether to fetch from API or return Mock data
   * based on the current system mode.
   * Default Strategy: Local First.
   */
  public async execute<T>(
    endpoint: string, 
    mockData: T | (() => Promise<T>), 
    options: RequestOptions = {}
  ): Promise<T> {
    const mode = this.getMode();

    // 1. LOCAL MODE - Direct Mock (Strict Default)
    // If mode is local, we skip network entirely and use the provided mockData/localDB function.
    if (mode === 'local') {
      if (options.mockDelay) await new Promise(r => setTimeout(r, options.mockDelay));
      return this.resolveMock(mockData);
    }

    // 2. SERVER & AUTO MODE - Try Fetch
    // Only attempts network if explicitly set to 'server' or 'auto'.
    try {
      return await this.request<T>(endpoint, options);
    } catch (error) {
      // 3. ERROR FALLBACK
      // If server is unreachable, we log a warning but keep the app alive by falling back to local data.
      console.warn(`[API] Request to ${endpoint} failed. Mode: ${mode}. Falling back to local data.`, error);
      
      // If the user *strictly* wants server mode, we might want to throw, 
      // but for app stability, we currently fallback to local.
      // Uncomment the line below if 'server' mode should crash on error:
      // if (mode === 'server') throw error;
      
      if (options.mockDelay) await new Promise(r => setTimeout(r, options.mockDelay));
      return this.resolveMock(mockData);
    }
  }

  private async resolveMock<T>(mockData: T | (() => Promise<T>)): Promise<T> {
    return typeof mockData === 'function' ? (mockData as Function)() : mockData;
  }

  private async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const token = this.getAuthToken();
    const lang = this.getLanguage();
    
    // Construct Query Params
    const queryParams = new URLSearchParams(options.params || {});
    if (!queryParams.has('lg')) {
        queryParams.append('lg', lang);
    }

    const queryString = queryParams.toString();
    const fullUrl = `${this.baseUrl}${endpoint}${queryString ? `?${queryString}` : ''}`;

    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers as any || {}),
    };

    let body = options.body;
    if (body && typeof body === 'object' && 
        !(body instanceof Blob) && 
        !(body instanceof FormData) && 
        !(body instanceof URLSearchParams) && 
        !(body instanceof ArrayBuffer) && 
        !ArrayBuffer.isView(body)) {
      body = JSON.stringify(body);
    }

    const config: RequestInit = {
      ...options,
      headers,
      body: body as BodyInit,
    };

    try {
      const response = await fetch(fullUrl, config);
      if (response.status === 401) throw new Error('Unauthorized');
      if (!response.ok) {
        try {
            const errorData = await response.json();
            throw new Error(errorData.detail || errorData.title || `HTTP Error ${response.status}`);
        } catch (e) {
            throw new Error(`HTTP Error ${response.status}`);
        }
      }
      if (response.status === 204) return {} as T;
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  public get<T>(endpoint: string, options?: RequestOptions) {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }
  
  public post<T>(endpoint: string, body: any, options?: RequestOptions) {
    return this.request<T>(endpoint, { ...options, method: 'POST', body });
  }

  public put<T>(endpoint: string, body: any, options?: RequestOptions) {
    return this.request<T>(endpoint, { ...options, method: 'PUT', body });
  }

  public delete<T>(endpoint: string, options?: RequestOptions) {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

// Singleton Instance
export const api = new ApiClient(
  'http://localhost:5268', 
  () => {
    try {
        const user = localStorage.getItem('lahja_user');
        return user ? JSON.parse(user).token : null;
    } catch { return null; }
  },
  // Determine Mode: Default to 'local' strictly unless config exists and says otherwise
  () => {
    try {
      const config = localStorage.getItem('lahja_config');
      if (!config) return 'local'; 
      const parsed = JSON.parse(config);
      return parsed.dataSourceMode || 'local';
    } catch { return 'local'; }
  },
  () => {
      try {
        const config = localStorage.getItem('lahja_config');
        return config ? JSON.parse(config).language : 'ar';
      } catch { return 'ar'; }
  }
);
